import { forceReissueR2SessionToken, getSessionToken } from "@/features/bucket/api";
import { BACKEND_API_URL } from "@/shared/config/app";
import { SESSION_ID_RE } from "../lib/session";
import type {
    FoodPackOffer,
    FoodPackState,
    FoodProduct,
    MaybeProduct,
    NextQuestion,
    Quota,
    QuestionKind,
    QuestionOption,
    RecognizeAnswers,
    RecognizeResult,
    ScaleContainer,
    ScaleReading,
    StateKey,
} from "../types";

export { newSessionId, SESSION_ID_RE } from "../lib/session";
export type { PhotoKind, RecognizeAnswers } from "../types";

/**
 * Распознавание блюда по фото: site_server POST /api/food/recognize
 * (multipart) и GET /api/food/quota. Ключи моделей — только на сервере.
 * Договор — routers_fastapi/food_recognize.py (docstring); настоящий ответ —
 * __fixtures__/recognize.contract.json (тест recognize.contract.test.ts).
 */

export const RECOGNIZE_URL = `${BACKEND_API_URL}/api/food/recognize`;
export const QUOTA_URL = `${BACKEND_API_URL}/api/food/quota`;

/** Модель думает десятки секунд; ждём с запасом, потом — «timeout». */
export const RECOGNIZE_TIMEOUT_MS = 90_000;

/** Фото в одном блюде — не больше, чем принимает сервер (food_recognize.MAX_IMAGES). */
export const MAX_IMAGES = 6;

export type RecognizeErrorKind =
    | "quota"
    | "no_diagnostic"
    | "rerun_limit"
    | "daily_calls"
    | "too_many"
    | "busy"
    | "unavailable"
    | "timeout"
    | "auth"
    | "network"
    | "server";

export class RecognizeError extends Error {
    kind: RecognizeErrorKind;
    status?: number;
    /** Код ошибки сервера («daily_limit», «no_diet_diagnostic»…). */
    code?: string;
    /** Счётчик, если сервер его прислал (402, 403 no_diet_diagnostic, 429 session_rerun_limit и daily_calls). */
    quota?: Quota;
    /** 413 too_many_images: сколько фото принимает сервер. */
    maxImages?: number;
    constructor(
        kind: RecognizeErrorKind,
        message: string,
        extra: { status?: number; quota?: Quota; code?: string; maxImages?: number } = {},
    ) {
        super(message);
        this.name = "RecognizeError";
        this.kind = kind;
        this.status = extra.status;
        this.quota = extra.quota;
        this.code = extra.code;
        if (extra.maxImages != null) this.maxImages = extra.maxImages;
    }
}

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null && !Array.isArray(v);
const num = (v: unknown): number | undefined => (typeof v === "number" && isFinite(v) ? v : typeof v === "string" && v.trim() !== "" && isFinite(+v) ? +v : undefined);
const str = (v: unknown): string | undefined => (typeof v === "string" && v.trim() ? v : undefined);
const idOrNull = (v: unknown): number | null => num(v) ?? null;

/** Код ошибки из тела: {error} или {detail: {error}} / {detail: "код"}. */
const codeOf = (body: unknown): string => {
    if (!isRecord(body)) return "";
    return str(body.error) ?? (isRecord(body.detail) ? str(body.detail.error) : str(body.detail)) ?? "";
};

/** 403 с этими кодами — не устаревший токен: повтор с новым не поможет. */
const FINAL_403 = new Set(["no_diet_diagnostic", "not_admin", "session_not_allowed"]);

/**
 * 503 сервера «временно недоступно» (+Retry-After): не подтверждён запрет записи диагностики (diagnostic_unavailable,
 * раздел открыт всем — платное только при подтверждённом запрете) или не записался счётчик (quota_unavailable).
 * Модель не звалась; это не «нет диагностики» — заказ диагностики не предлагаем.
 */
const UNAVAILABLE_503 = new Set(["diagnostic_unavailable", "quota_unavailable"]);

const peekCode = async (res: Response): Promise<string> => {
    if (typeof res.clone !== "function") return "";
    try {
        return codeOf(await res.clone().json());
    } catch {
        return "";
    }
};

/**
 * Запрос с токеном сессии и одним повтором после 401/403 через
 * forceReissueR2SessionToken() — как uploadMedia (communityGroups/api/mediaApi.ts).
 * 403 «нет диагностики» / «не админ» не повторяем. Только Authorization:
 * multipart-границу FormData выставляет сам браузер.
 */
export const sendAuthed = async (url: string, init: RequestInit = {}): Promise<Response> => {
    const send = () =>
        fetch(url, {
            ...init,
            headers: { ...((init.headers as Record<string, string>) || {}), Authorization: `Bearer ${getSessionToken()}` },
        });
    let res = await send();
    if (res.status === 401 || (res.status === 403 && !FINAL_403.has(await peekCode(res)))) {
        if (await forceReissueR2SessionToken()) res = await send();
    }
    return res;
};

const STATE_KEYS = new Set(["U", "B", "Bk", "Fr", "St", "Dr"]);
const STATE_BY_NAME: Record<string, StateKey> = { uncooked: "U", boiled: "B", baked: "Bk", fried: "Fr", steamed: "St", dried: "Dr" };
const stateOf = (v: unknown): StateKey | undefined => {
    if (typeof v !== "string") return undefined;
    return STATE_KEYS.has(v) ? (v as StateKey) : STATE_BY_NAME[v];
};
/** Доля 0…1; сервер может прислать проценты. */
const frac = (v: number | undefined, percent: boolean) => (v == null ? undefined : percent ? v / 100 : v);
const prob01 = (v: unknown) => {
    const p = num(v);
    return p == null ? 0 : p > 1 ? p / 100 : p;
};

/** offer сервера: всё, что показываем, — с сервера; без числа, блюд в день или цены — предложения нет. */
const mapOffer = (v: unknown): FoodPackOffer | null => {
    if (!isRecord(v)) return null;
    const count = num(v.count);
    const days = num(v.days);
    const dailyLimit = num(v.daily_limit);
    const price = num(v.price);
    const currency = str(v.currency);
    if (count == null || count <= 0 || days == null || days <= 0 || price == null || price <= 0 || !currency) return null;
    if (dailyLimit == null || dailyLimit <= 0) return null;
    return {
        product: str(v.product) ?? "",
        count,
        days,
        dailyLimit,
        price,
        currency: currency.toUpperCase(),
        startsAt: str(v.starts_at) ?? null,
    };
};

const mapPack = (v: unknown): FoodPackState | undefined => {
    if (!isRecord(v)) return undefined;
    const dailyLimit = num(v.daily_limit);
    return { active: v.active === true, until: str(v.until) ?? null, dailyLimit: dailyLimit != null && dailyLimit > 0 ? dailyLimit : 0 };
};

/**
 * Quota сервера → Quota. Прежний счётчик месяца — как был ({used, limit, month, resets, eligible, reason}); поля
 * счётчика дня (period, day, resets_at, tz, pack, offer) — только если сервер их прислал.
 */
export const mapQuota = (q: unknown): Quota | undefined => {
    if (!isRecord(q)) return undefined;
    const used = num(q.used);
    const limit = num(q.limit);
    if (used == null || limit == null) return undefined;
    const out: Quota = { used, limit, month: str(q.month) ?? "", resets: str(q.resets) ?? "", eligible: q.eligible !== false };
    const reason = str(q.reason);
    if (reason) out.reason = reason;
    if (q.period === "day" || q.period === "month") out.period = q.period;
    const day = str(q.day);
    if (day) out.day = day;
    const resetsAt = str(q.resets_at);
    if (resetsAt) out.resetsAt = resetsAt;
    const tz = str(q.tz);
    if (tz) out.tz = tz;
    const pack = mapPack(q.pack);
    if (pack) out.pack = pack;
    // купить может только тот, у кого есть диагностика диеты (сервер и так шлёт null)
    if ("offer" in q) out.offer = out.eligible ? mapOffer(q.offer) : null;
    return out;
};

/** [{product_id, name, prob}] — alts продукта и maybe; без id — отброшены. */
const mapAlts = (v: unknown): MaybeProduct[] =>
    (Array.isArray(v) ? v.filter(isRecord) : []).flatMap((m) => {
        const productId = num(m.product_id);
        if (productId == null) return [];
        return [{ productId, name: str(m.name) ?? "", prob: prob01(m.prob) }];
    });

const QUESTION_KINDS = new Set<string>(["liquid", "fat", "sweet", "presence"]);

/** next_question: вид, о каком продукте, текст, варианты; без текста или вариантов — нет вопроса. */
export const mapQuestion = (v: unknown): NextQuestion | undefined => {
    if (!isRecord(v)) return undefined;
    const text = str(v.text);
    const kind = typeof v.kind === "string" ? v.kind : "";
    if (!text || !QUESTION_KINDS.has(kind)) return undefined;
    const options: QuestionOption[] = (Array.isArray(v.options) ? v.options.filter(isRecord) : []).flatMap((o) => {
        const label = str(o.label);
        return label ? [{ label, productId: idOrNull(o.product_id) }] : [];
    });
    if (!options.length) return undefined;
    return { kind: kind as QuestionKind, productId: idOrNull(v.product_id), text, options };
};

const SCALE_CONTAINERS = new Set<string>(["plate", "bowl", "pot", "none"]);

/** Весы под блюдом: число 1…10000 г, иначе весов нет (сервер так и чистит — здесь на случай старого или чужого ответа). */
export const mapScale = (v: unknown): ScaleReading | undefined => {
    if (!isRecord(v)) return undefined;
    const grams = num(v.grams);
    if (grams == null || grams < 1 || grams > 10_000) return undefined;
    const container = typeof v.container === "string" && SCALE_CONTAINERS.has(v.container) ? (v.container as ScaleContainer) : null;
    const box = num(v.container_grams);
    const water = num(v.water_grams);
    return {
        grams: Math.round(grams),
        container,
        tared: typeof v.tared === "boolean" ? v.tared : null,
        containerGrams: box != null && box >= 0 ? Math.round(box) : 0,
        waterGrams: water != null && water >= 0 ? Math.min(Math.round(water), Math.round(grams)) : 0,
    };
};

/** Ответ сервера (snake_case) → RecognizeResult; продукты без product_id отбрасываются. */
export const mapRecognize = (json: unknown): RecognizeResult => {
    const j = isRecord(json) ? json : {};
    const rawProducts = Array.isArray(j.products) ? j.products.filter(isRecord) : [];
    const sharePercent = rawProducts.some((p) => (num(p.share) ?? 0) > 1.5);
    const products: FoodProduct[] = [];
    rawProducts.forEach((p) => {
        const productId = num(p.product_id);
        if (productId == null) return;
        const out: FoodProduct = {
            productId,
            name: str(p.name) ?? "",
            grams: num(p.grams) ?? 0,
            share: frac(num(p.share), sharePercent) ?? 0,
            confidence: frac(num(p.confidence), (num(p.confidence) ?? 0) > 1) ?? 1,
        };
        const count = num(p.count);
        if (count != null) out.count = count;
        const unit = str(p.unit);
        if (unit) out.unit = unit;
        const qtySd = num(p.qty_sd);
        if (qtySd != null) out.qtySd = qtySd;
        const state = stateOf(p.state);
        if (state) out.state = state;
        const alts = mapAlts(p.alts).filter((a) => a.productId !== productId);
        if (alts.length) out.alts = alts;
        products.push(out);
    });
    // доли не пришли — считаем по граммам
    const g = products.reduce((s, p) => s + p.grams, 0);
    if (g > 0 && products.every((p) => !p.share)) products.forEach((p) => (p.share = p.grams / g));
    const res: RecognizeResult = { products, maybe: mapAlts(j.maybe) };
    const dishGuess = str(j.dish_guess);
    if (dishGuess) res.dishGuess = dishGuess;
    if (typeof j.hot === "boolean") res.hot = j.hot;
    const nextQuestion = mapQuestion(j.next_question);
    if (nextQuestion) res.nextQuestion = nextQuestion;
    const scale = mapScale(j.scale);
    if (scale) res.scale = scale;
    const model = str(j.model);
    if (model) res.model = model;
    const quota = mapQuota(j.quota);
    if (quota) res.quota = quota;
    const sessionId = str(j.session_id);
    if (sessionId) res.sessionId = sessionId;
    const left = num(j.session_reruns_left);
    if (left != null) res.sessionRerunsLeft = left;
    return res;
};

const readJson = async (res: Response): Promise<unknown> => {
    try {
        return await res.json();
    } catch {
        return null;
    }
};

/**
 * Ошибка сервера {error, quota?} (или {detail} у HTTPException) → вид:
 * 402 (daily_limit, прежний quota_exceeded) → quota; 403 no_diet_diagnostic → no_diagnostic;
 * 429 session_rerun_limit → rerun_limit; 429 too_many_requests с reason "daily_calls" (вызовов модели за день
 * больше предела сервера, food_quota.call_cap — до полуночи UTC, с quota) → daily_calls; 413 too_many_images →
 * too_many; прочие 429 → busy; 503 diagnostic_unavailable / quota_unavailable → unavailable («временно недоступно»);
 * 401/403 после повтора → auth; остальное → server.
 */
const errorOf = async (res: Response): Promise<RecognizeError> => {
    const body = await readJson(res);
    const b = isRecord(body) ? body : {};
    const code = codeOf(b);
    const quota = (isRecord(b.detail) ? mapQuota(b.detail.quota) : undefined) ?? mapQuota(b.quota);
    const extra = { status: res.status, code, quota };
    if (res.status === 402 || code === "quota_exceeded" || code === "daily_limit") return new RecognizeError("quota", code || "daily_limit", extra);
    if (code === "no_diet_diagnostic") return new RecognizeError("no_diagnostic", code, extra);
    if (code === "session_rerun_limit") return new RecognizeError("rerun_limit", code, extra);
    // вызовы модели на сегодня кончились (и уточнения тоже): «подождите немного» не поможет — до нового дня сервера
    const reason = str(b.reason) ?? (isRecord(b.detail) ? str(b.detail.reason) : undefined);
    if (res.status === 429 && reason === "daily_calls") return new RecognizeError("daily_calls", code || "too_many_requests", extra);
    if (code === "too_many_images") {
        const max = num(b.max_images) ?? (isRecord(b.detail) ? num(b.detail.max_images) : undefined);
        return new RecognizeError("too_many", code, { ...extra, maxImages: max ?? MAX_IMAGES });
    }
    if (res.status === 429) return new RecognizeError("busy", code || "HTTP 429", extra);
    if (res.status === 503 && UNAVAILABLE_503.has(code)) return new RecognizeError("unavailable", code, extra);
    if (res.status === 401 || res.status === 403) return new RecognizeError("auth", `HTTP ${res.status}`, extra);
    return new RecognizeError("server", code || `HTTP ${res.status}`, extra);
};

const call = async (url: string, init: RequestInit, timeoutMs?: number): Promise<unknown> => {
    // свой контроллер: снаружи отменили (новое фото, закрыли лист) — отменяем; дольше timeoutMs — «timeout»
    const outer = init.signal;
    const ctrl = new AbortController();
    let timedOut = false;
    const onAbort = () => ctrl.abort();
    if (outer) {
        if (outer.aborted) ctrl.abort();
        else outer.addEventListener("abort", onAbort);
    }
    const timer = timeoutMs
        ? setTimeout(() => {
              timedOut = true;
              ctrl.abort();
          }, timeoutMs)
        : null;
    try {
        let res: Response;
        try {
            res = await sendAuthed(url, { ...init, signal: ctrl.signal });
        } catch (err) {
            if (timedOut) throw new RecognizeError("timeout", "timeout");
            if (err instanceof Error && err.name === "AbortError") throw err;
            throw new RecognizeError("network", err instanceof Error ? err.message : "network");
        }
        if (!res.ok) throw await errorOf(res);
        const json = await readJson(res);
        if (timedOut) throw new RecognizeError("timeout", "timeout");
        if (json == null) throw new RecognizeError("server", "not json", { status: res.status });
        return json;
    } finally {
        if (timer) clearTimeout(timer);
        outer?.removeEventListener("abort", onAbort);
    }
};

/** Длина названия блюда, которую отправляем серверу. */
export const DISH_NAME_MAX = 120;

/** Языки сервера: ru | en | de | es | zh (у сайта китайский — «cn»). */
export const serverLang = (lang: string) => {
    const l = (lang || "ru").slice(0, 2).toLowerCase();
    if (l === "cn") return "zh";
    return ["ru", "en", "de", "es", "zh"].includes(l) ? l : "ru";
};

export interface RecognizeOptions {
    dishName?: string;
    answers?: RecognizeAnswers;
    lang: string;
    /** id блюда: одно блюдо — одно распознавание, уточнения с тем же id бесплатны. */
    sessionId?: string;
    signal?: AbortSignal;
    timeoutMs?: number;
}

/** Пустое (пустые массивы и объекты, undefined, "") не шлём; ничего не осталось — null. */
const compactAnswers = (a: RecognizeAnswers | undefined) => {
    if (!a) return null;
    const out: Record<string, unknown> = {};
    Object.entries(a).forEach(([k, v]) => {
        if (v == null || v === "" || v === false || (Array.isArray(v) && !v.length) || (isRecord(v) && !Object.keys(v).length)) return;
        out[k] = v;
    });
    return Object.keys(out).length ? out : null;
};

/** Фото — уже уменьшенные (shrinkImage), по порядку; что на каждом — answers.kinds (без него последнее — готовое блюдо). */
export const recognize = async (files: Blob[], opts: RecognizeOptions): Promise<RecognizeResult> => {
    const form = new FormData();
    files.forEach((f, i) => form.append("images", f, `photo${i + 1}.jpg`));
    // название — подсказка модели, а не инструкция: длинный текст не шлём (сервер режет тоже)
    const dishName = (opts.dishName || "").replace(/\s+/g, " ").trim().slice(0, DISH_NAME_MAX);
    if (dishName) form.append("dish_name", dishName);
    const answers = compactAnswers(opts.answers);
    if (answers) form.append("answers", JSON.stringify(answers));
    form.append("lang", serverLang(opts.lang));
    if (opts.sessionId && SESSION_ID_RE.test(opts.sessionId)) form.append("session_id", opts.sessionId);
    return mapRecognize(
        await call(RECOGNIZE_URL, { method: "POST", body: form, signal: opts.signal }, opts.timeoutMs ?? RECOGNIZE_TIMEOUT_MS),
    );
};

export const getQuota = async (signal?: AbortSignal): Promise<Quota> => {
    const q = mapQuota(await call(QUOTA_URL, { method: "GET", cache: "no-store", signal }, 20_000));
    if (!q) throw new RecognizeError("server", "bad quota");
    return q;
};

// ------------------------------------------------------------------ уменьшение фото

export const SHRINK_LONG_SIDE = 1024;
export const SHRINK_QUALITY = 0.85;

/** Браузер не смог прочитать картинку (обычно HEIC вне Safari): попросите JPEG или снимок камерой. */
export class ImageDecodeError extends Error {
    heic: boolean;
    constructor(heic: boolean) {
        super(heic ? "heic_unsupported" : "image_unreadable");
        this.name = "ImageDecodeError";
        this.heic = heic;
    }
}

const isHeic = (f: Blob & { name?: string }) =>
    /hei[cf]/i.test(f.type) || /\.hei[cf]$/i.test(f.name || "");

type Drawable = { width: number; height: number; source: CanvasImageSource; close?: () => void };

const decode = async (file: Blob): Promise<Drawable> => {
    if (typeof createImageBitmap === "function") {
        try {
            // "from-image" (поворот по EXIF) новее типов DOM этой версии TypeScript
            const bmp = await createImageBitmap(file, { imageOrientation: "from-image" } as unknown as ImageBitmapOptions);
            return { width: bmp.width, height: bmp.height, source: bmp, close: () => bmp.close() };
        } catch {
            /* ниже — через <img> */
        }
    }
    const url = URL.createObjectURL(file);
    try {
        const img = await new Promise<HTMLImageElement>((resolve, reject) => {
            const el = new Image();
            el.onload = () => resolve(el);
            el.onerror = () => reject(new Error("decode"));
            el.src = url;
        });
        return { width: img.naturalWidth, height: img.naturalHeight, source: img };
    } finally {
        URL.revokeObjectURL(url);
    }
};

/**
 * Фото для отправки: длинная сторона не больше 1024 px, JPEG 0,85. Перерисовка
 * на canvas выкидывает EXIF (место съёмки, телефон); поворот из EXIF браузер
 * применяет при декодировании. Не читается → ImageDecodeError (heic = true для HEIC).
 */
export const shrinkImage = async (file: Blob & { name?: string }): Promise<Blob> => {
    let img: Drawable;
    try {
        img = await decode(file);
    } catch {
        throw new ImageDecodeError(isHeic(file));
    }
    try {
        if (!img.width || !img.height) throw new ImageDecodeError(isHeic(file));
        const k = Math.min(1, SHRINK_LONG_SIDE / Math.max(img.width, img.height));
        const w = Math.max(1, Math.round(img.width * k));
        const h = Math.max(1, Math.round(img.height * k));
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        if (!ctx) throw new ImageDecodeError(false);
        ctx.drawImage(img.source, 0, 0, w, h);
        const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/jpeg", SHRINK_QUALITY));
        if (!blob) throw new ImageDecodeError(false);
        return blob;
    } finally {
        img.close?.();
    }
};
